import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import MobileCTA from './components/MobileCTA';

// Immediate Load
import Home from './pages/Home';

// Lazy Load
const Pricing = lazy(() => import('./pages/Pricing'));
const Industries = lazy(() => import('./pages/Industries'));
const Features = lazy(() => import('./pages/Features'));
const CaseStudies = lazy(() => import('./pages/CaseStudies'));
const Demo = lazy(() => import('./pages/Demo'));
const BookDemo = lazy(() => import('./pages/BookDemo'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));

// Solution Pillar Pages
const AISalesAutomation = lazy(() => import('./pages/AISalesAutomation'));
const AIAppointmentBooking = lazy(() => import('./pages/AIAppointmentBooking'));
const AIFollowUpAutomation = lazy(() => import('./pages/AIFollowUpAutomation'));
const AILeadResponseAutomation = lazy(() => import('./pages/AILeadResponseAutomation'));
const AICRMAutomation = lazy(() => import('./pages/AICRMAutomation'));
const AICustomerCommunicationAutomation = lazy(() => import('./pages/AICustomerCommunicationAutomation'));
const AILeadConversionAutomation = lazy(() => import('./pages/AILeadConversionAutomation'));
const AISalesFunnelAutomation = lazy(() => import('./pages/AISalesFunnelAutomation'));
const AIChatAutomation = lazy(() => import('./pages/AIChatAutomation'));

// Industry Pages
const MedSpa = lazy(() => import('./pages/industries/MedSpa'));
const Roofing = lazy(() => import('./pages/industries/Roofing'));
const Dentist = lazy(() => import('./pages/industries/Dentist'));
const RealEstate = lazy(() => import('./pages/industries/RealEstate'));
const LawFirm = lazy(() => import('./pages/industries/LawFirm'));
const Solar = lazy(() => import('./pages/industries/Solar'));
const HVAC = lazy(() => import('./pages/industries/HVAC'));
const Automotive = lazy(() => import('./pages/industries/Automotive'));

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const PageLoader = () => (
  <div className="min-h-screen bg-brand-dark flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-brand-cyan/20 border-t-brand-cyan rounded-full animate-spin"></div>
  </div>
);

function App() {
  return (
    <Router>
      <ScrollToTop />
      <div className="flex flex-col min-h-screen bg-brand-dark overflow-x-hidden">
        <Navigation />
        
        <main className="flex-grow">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/industries" element={<Industries />} />
              <Route path="/features" element={<Features />} />
              <Route path="/case-studies" element={<CaseStudies />} />
              
              {/* Solution Specific Routes */}
              <Route path="/ai-sales-automation" element={<AISalesAutomation />} />
              <Route path="/ai-appointment-booking" element={<AIAppointmentBooking />} />
              <Route path="/ai-appointment-booking-automation" element={<AIAppointmentBooking />} />
              <Route path="/ai-follow-up-automation" element={<AIFollowUpAutomation />} />
              <Route path="/ai-lead-response-automation" element={<AILeadResponseAutomation />} />
              <Route path="/ai-crm-automation" element={<AICRMAutomation />} />
              <Route path="/ai-customer-communication-automation" element={<AICustomerCommunicationAutomation />} />
              <Route path="/ai-lead-conversion-automation" element={<AILeadConversionAutomation />} />
              <Route path="/ai-sales-funnel-automation" element={<AISalesFunnelAutomation />} />
              <Route path="/ai-chat-automation-for-businesses" element={<AIChatAutomation />} />
              
              {/* Industry Specific Routes */}
              <Route path="/industries/med-spa-ai-automation" element={<MedSpa />} />
              <Route path="/industries/roofing-ai-automation" element={<Roofing />} />
              <Route path="/industries/dentist-ai-automation" element={<Dentist />} />
              <Route path="/industries/real-estate-ai-automation" element={<RealEstate />} />
              <Route path="/industries/law-firm-ai-automation" element={<LawFirm />} />
              <Route path="/industries/solar-ai-automation" element={<Solar />} />
              <Route path="/industries/hvac-ai-automation" element={<HVAC />} />
              <Route path="/industries/automotive-ai-automation" element={<Automotive />} />
              
              {/* General Routes */}
              <Route path="/demo" element={<Demo />} />
              <Route path="/book-demo" element={<BookDemo />} />
              <Route path="/contact" element={<BookDemo />} />
              <Route path="/about" element={<Features />} /> {/* Redirecting About to Features for now */}
              <Route path="/resources" element={<CaseStudies />} /> {/* Redirecting Resources to Case Studies */}
              
              {/* Legal Routes */}
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
            </Routes>
          </Suspense>
        </main>

        <Footer />
        <MobileCTA />
      </div>
    </Router>
  );
}

export default App;